import { useEffect, useRef, useState } from 'react';
import { Alert, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { getApiErrorMessage } from '@shared/utils/apiError.utils';
import { useWorkspaceScope } from './useWorkspaceScope';
import {
  usePreviewWorkspaceMissionEventMutation,
  type MissionEventField,
  type MissionEventFilters as Filters,
  type MissionSpec,
} from './workspaceApi';

const fields: [MissionEventField, string][] = [
  ['campaignId', 'Campaign ID'],
  ['type', 'Native call type'],
  ['direction', 'Phone direction'],
  ['status', 'Call status'],
  ['endedReason', 'Ended reason'],
  ['agentId', 'Agent ID'],
  ['squadId', 'Squad ID'],
];
const operators = [
  ['equals', 'Equals'],
  ['not-equals', 'Does not equal'],
  ['contains', 'Contains'],
  ['exists', 'Is present'],
] as const;
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const MissionEventFilters = ({
  spec,
  disabled,
  onChange,
}: {
  spec: MissionSpec;
  disabled: boolean;
  onChange: (filters: Filters | undefined) => void;
}) => {
  const scope = useWorkspaceScope();
  const filters = spec.trigger.filters ?? { mode: 'all', conditions: [] };
  const [callId, setCallId] = useState('');
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState<{
    fingerprint: string;
    metadata?: Partial<Record<MissionEventField, string | null>>;
    matches?: boolean;
    error?: string;
  } | null>(null);
  const [preview] = usePreviewWorkspaceMissionEventMutation();
  const alive = useRef(true);
  const busy = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const fingerprint = JSON.stringify([scope, spec, callId]);
  const current = result?.fingerprint === fingerprint ? result : null;
  const inspect = async () => {
    if (busy.current || !uuid.test(callId)) return;
    busy.current = true;
    setWorking(true);
    setResult(null);
    try {
      const response = await preview({ spec, callId }).unwrap();
      if (alive.current) setResult({ fingerprint, ...response });
    } catch (e) {
      if (alive.current)
        setResult({
          fingerprint,
          error: getApiErrorMessage(
            e,
            'Unable to inspect this ended call in the selected campaign scope.',
          ),
        });
    } finally {
      busy.current = false;
      if (alive.current) setWorking(false);
    }
  };
  const patch = (index: number, value: Partial<Filters['conditions'][number]>) =>
    onChange({
      ...filters,
      conditions: filters.conditions.map((condition, at) =>
        at === index ? { ...condition, ...value } : condition,
      ),
    });
  return (
    <Stack gap={2}>
      <Typography variant="subtitle2">Event conditions</Typography>
      <TextField
        label="Combine event conditions"
        select
        value={filters.mode}
        disabled={disabled}
        onChange={(e) => onChange({ ...filters, mode: e.target.value as Filters['mode'] })}>
        <MenuItem value="all">Match all conditions (AND)</MenuItem>
        <MenuItem value="any">Match any condition (OR)</MenuItem>
      </TextField>
      {!filters.conditions.length && (
        <Typography variant="body2" color="text.secondary">
          Every persisted ended call in the selected campaigns can trigger this mission.
        </Typography>
      )}
      {filters.conditions.map((condition, index) => (
        <Paper variant="outlined" sx={{ p: 2 }} key={index}>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
            <TextField
              fullWidth
              label={`Event field ${index + 1}`}
              select
              value={condition.field}
              disabled={disabled}
              onChange={(e) => patch(index, { field: e.target.value as MissionEventField })}>
              {fields.map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label={`Event operator ${index + 1}`}
              select
              value={condition.operator}
              disabled={disabled}
              onChange={(e) =>
                patch(index, {
                  operator: e.target.value as typeof condition.operator,
                  ...(e.target.value === 'exists' ? { value: '' } : {}),
                })
              }>
              {operators.map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            {condition.operator !== 'exists' && (
              <TextField
                fullWidth
                label={`Event value ${index + 1}`}
                value={condition.value}
                disabled={disabled}
                error={!condition.value.trim()}
                slotProps={{ htmlInput: { maxLength: 200 } }}
                onChange={(e) => patch(index, { value: e.target.value })}
              />
            )}
            <Button
              disabled={disabled}
              aria-label={`Remove event condition ${index + 1}`}
              onClick={() =>
                onChange({
                  ...filters,
                  conditions: filters.conditions.filter((_, at) => at !== index),
                })
              }>
              Remove
            </Button>
          </Stack>
        </Paper>
      ))}
      <Stack direction="row" gap={1}>
        <Button
          disabled={disabled || filters.conditions.length >= 20}
          onClick={() =>
            onChange({
              ...filters,
              conditions: [
                ...filters.conditions,
                { field: 'status', operator: 'equals', value: '' },
              ],
            })
          }>
          Add event condition
        </Button>
        <Button disabled={disabled || !spec.trigger.filters} onClick={() => onChange(undefined)}>
          Clear conditions
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Values match exactly and are case-sensitive. Missing metadata never satisfies a negative
        condition. Phone direction is absent for browser calls.
      </Typography>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack gap={2}>
          <Typography variant="subtitle2">Check a saved ended call</Typography>
          <TextField
            label="Saved call ID"
            value={callId}
            disabled={working}
            slotProps={{ htmlInput: { maxLength: 36 } }}
            helperText="Use an ended call from a campaign included in this mission. No event or action will be created."
            onChange={(e) => setCallId(e.target.value.trim())}
          />
          <Button
            disabled={working || !uuid.test(callId)}
            onClick={() => void inspect()}
            sx={{ alignSelf: 'start' }}>
            {working ? 'Checking saved event…' : 'Check event match'}
          </Button>
          {current?.error && <Alert severity="error">{current.error}</Alert>}
          {current?.matches !== undefined && (
            <Alert severity={current.matches ? 'success' : 'info'}>
              {current.matches
                ? 'This saved event matches the current conditions.'
                : 'This saved event does not match the current conditions.'}
            </Alert>
          )}
          {current?.metadata &&
            fields.map(([key, label]) => (
              <Typography key={key} variant="body2">
                {label}: {current.metadata?.[key] || 'Not present'}
              </Typography>
            ))}
          {result && !current && (
            <Alert severity="warning">
              The draft, call or account changed. Check the event match again.
            </Alert>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
};
